import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, Zap, CheckCircle, AlertCircle, X, DollarSign, ArrowRight, Shield
} from "lucide-react";

import { fmtUSD } from "../../utils/currency";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };

export default function ExpressCommitModal({ opp, onClose, onSuccess }) {
  const [step, setStep] = useState("input"); // input → preview → confirmed
  const [amount, setAmount] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const handlePreview = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < opp.min_investment) {
      setError(`Minimum investment is ${fmtUSD(parseInt(opp.min_investment))}`);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/investor/express-commit`, {
        opportunity_id: opp.opportunity_id, amount: amt, confirm: false
      }, { headers });
      setPreview(res.data);
      setStep("preview");
    } catch (err) { setError(err.response?.data?.detail || "Failed to preview"); }
    finally { setLoading(false); }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/investor/express-commit`, {
        opportunity_id: opp.opportunity_id, amount: parseFloat(amount), confirm: true
      }, { headers });
      setStep("confirmed");
      setTimeout(() => onSuccess(), 1800);
    } catch (err) { setError(err.response?.data?.detail || "Failed to commit"); }
    finally { setLoading(false); }
  };

  const snap = opp.deal_snapshot || {};

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()} data-testid="express-commit-modal">
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#e8e0d0] bg-gradient-to-r from-emerald-50 to-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-emerald-100 border border-emerald-200">
              <Zap className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-[#222]">Express Commit</h3>
              <p className="text-[10px] text-[#888]">Quick capital deployment</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#faf7f2] text-[#888]"><X className="h-4 w-4" /></button>
        </div>

        {step === "confirmed" ? (
          <div className="p-8 text-center">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", damping: 12 }}>
              <CheckCircle className="h-14 w-14 mx-auto text-emerald-600 mb-3" />
            </motion.div>
            <p className="font-bold text-lg text-[#222]">Commitment Confirmed</p>
            <p className="text-xs text-[#888] mt-1">You'll receive a notification when it's approved.</p>
          </div>
        ) : step === "preview" && preview ? (
          <div className="p-5 space-y-4">
            {/* Preview summary */}
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-emerald-600 font-medium">Your Investment</span>
                <span className="text-lg font-black text-emerald-700">${parseFloat(amount).toLocaleString()}</span>
              </div>
              <div className="h-px bg-emerald-200" />
              <div className="grid grid-cols-2 gap-3 text-center">
                <div>
                  <div className="text-base font-bold text-emerald-700">{preview.projected_yield}%</div>
                  <div className="text-[9px] text-emerald-600">Projected Yield</div>
                </div>
                <div>
                  <div className="text-base font-bold text-emerald-700">{preview.projected_term_months}mo</div>
                  <div className="text-[9px] text-emerald-600">Term</div>
                </div>
              </div>
              <div className="h-px bg-emerald-200" />
              <div className="flex items-center justify-between">
                <span className="text-xs text-emerald-600">Projected Return</span>
                <span className="text-sm font-bold text-emerald-700">+${preview.projected_return?.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-emerald-600 font-semibold">Total Back (est.)</span>
                <span className="text-base font-black text-emerald-800">${preview.total_back?.toLocaleString()}</span>
              </div>
            </div>

            <div className="rounded-lg bg-[#faf7f2] border border-[#e8e0d0] p-3 text-xs text-[#666]">
              <div className="flex items-center gap-1.5 mb-1">
                <Shield className="h-3 w-3 text-[#999]" />
                <span className="font-medium text-[#555]">Deal Info</span>
              </div>
              <p>{LOAN_LABELS[preview.loan_type] || preview.loan_type} in {preview.state} &mdash; {preview.property_address || "Property"}</p>
            </div>

            {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{error}</p>}

            <div className="flex gap-2">
              <button onClick={() => { setStep("input"); setPreview(null); }}
                className="px-4 py-2.5 rounded-xl border border-[#e8e0d0] text-sm text-[#666] hover:bg-[#faf7f2]">Back</button>
              <button onClick={handleConfirm} disabled={loading}
                className="flex-1 py-2.5 rounded-xl bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                data-testid="express-confirm-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Confirm Commitment
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="rounded-lg bg-[#faf7f2] border border-[#e8e0d0] p-3">
              <div className="flex justify-between text-xs">
                <span className="text-[#888]">Deal</span>
                <span className="font-medium text-[#333]">{LOAN_LABELS[snap.loan_type] || snap.loan_type} &mdash; {snap.state}</span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-[#888]">Target Raise</span>
                <span className="font-medium">${parseInt(opp.target_raise).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-[#888]">Min Investment</span>
                <span className="font-medium">${parseInt(opp.min_investment).toLocaleString()}</span>
              </div>
              {opp.projected_yield && (
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-[#888]">Projected Yield</span>
                  <span className="font-bold text-[#b89d5a]">{opp.projected_yield}%</span>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-[#444] block mb-1">Investment Amount ($)</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder={`Min ${fmtUSD(parseInt(opp.min_investment))}`}
                className="w-full px-3 py-2.5 rounded-xl border border-[#e8e0d0] text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none"
                data-testid="express-amount-input" />
            </div>

            {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{error}</p>}

            <div className="flex gap-2">
              <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-[#e8e0d0] text-sm text-[#666]">Cancel</button>
              <button onClick={handlePreview} disabled={loading}
                className="flex-1 py-2.5 rounded-xl bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                data-testid="express-preview-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Preview Commitment
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
